import clsx from 'clsx';
import { createContext, type ReactNode, useContext } from 'react';

import { Badge } from '../../core/badge/Badge';
import { TableBody, TableCell, type TableColumnWidth, TableGrid, TableRow } from '../../core/table/Table';
import { ProficiencyScale } from '../skill/ProficiencyScale';

export type DiffTableColumnWidths =
    | readonly [TableColumnWidth, TableColumnWidth]
    | readonly [TableColumnWidth, TableColumnWidth, TableColumnWidth];

const DEFAULT_COLUMN_WIDTHS: DiffTableColumnWidths = ['5.5rem', 'minmax(0,1fr)', '4.75rem'];

const DiffTableColumnWidthsContext = createContext<DiffTableColumnWidths>(DEFAULT_COLUMN_WIDTHS);

export type DiffTableProps = {
    readonly 'aria-label'?: string;
    readonly children: ReactNode;
    readonly className?: string;
    readonly columnWidths?: DiffTableColumnWidths;
};

export function DiffTable({ 'aria-label': ariaLabel, children, className, columnWidths }: DiffTableProps) {
    return (
        <DiffTableColumnWidthsContext.Provider value={columnWidths ?? DEFAULT_COLUMN_WIDTHS}>
            <TableGrid
                aria-label={ariaLabel}
                className={clsx('max-h-[min(280px,45vh)] overflow-y-auto', className)}
                role="grid"
            >
                <TableBody>{children}</TableBody>
            </TableGrid>
        </DiffTableColumnWidthsContext.Provider>
    );
}

export type DiffTableRowProps = {
    readonly center: ReactNode;
    readonly className?: string;
    readonly left?: ReactNode;
    /** Dim the row once its change has been resolved (accepted/declined). */
    readonly muted?: boolean;
    readonly right?: ReactNode;
};

export function DiffTableRow({ center, className, left, muted = false, right }: DiffTableRowProps) {
    const columnWidths = useContext(DiffTableColumnWidthsContext);
    const hasRightColumn = columnWidths.length === 3;
    return (
        <TableRow
            $columnWidths={columnWidths}
            className={clsx('!mt-xxs first:!mt-0 !gap-xxs !rounded-lg', muted && 'opacity-50', className)}
            role="row"
        >
            <TableCell className="!min-h-[34px] !items-start !py-xs" role="gridcell">
                {left}
            </TableCell>
            <TableCell className="!min-h-[34px] !min-w-0 !items-start !py-xs" role="gridcell">
                {center}
            </TableCell>
            {hasRightColumn ? (
                <TableCell className="!min-h-[34px] !items-start !justify-end !py-xs" role="gridcell">
                    {right}
                </TableCell>
            ) : null}
        </TableRow>
    );
}

// ---------------------------------------------------------------------------
// Skill-diff specialization
// ---------------------------------------------------------------------------

export type SkillDiffChange = 'add' | 'adjust' | 'remove';

export type SkillDiffRowData = {
    readonly change: SkillDiffChange;
    readonly id: string;
    /** Proposed proficiency level (target for add/adjust). */
    readonly level?: number | null;
    /** Existing proficiency level before the change (shown for remove/adjust). */
    readonly previousLevel?: number | null;
    /** Whether the change has been accepted/declined; dims the row. */
    readonly resolved?: boolean;
    readonly title: string;
    /** Secondary line, e.g. "Hard skill". */
    readonly typeLabel?: string;
};

const CHANGE_BADGE: Record<
    SkillDiffChange,
    { readonly label: string; readonly variant: 'error' | 'info' | 'success' }
> = {
    add: { label: 'Add', variant: 'success' },
    adjust: { label: 'Adjust', variant: 'info' },
    remove: { label: 'Remove', variant: 'error' },
};

function SkillDiffProficiency({
    change,
    level,
    previousLevel,
    scaleCount,
}: {
    readonly change: SkillDiffChange;
    readonly level?: number | null;
    readonly previousLevel?: number | null;
    readonly scaleCount: number;
}) {
    // Removed skills read out the proficiency they had (greyed); added/adjusted
    // skills highlight the proposed target.
    if (change === 'remove') {
        return <ProficiencyScale active={false} level={previousLevel} scaleCount={scaleCount} />;
    }
    return <ProficiencyScale active={true} level={level} scaleCount={scaleCount} />;
}

const TWO_COLUMN_WIDTHS: DiffTableColumnWidths = ['5.5rem', 'minmax(0,1fr)'];

const rowHasLevel = (row: SkillDiffRowData): boolean =>
    typeof row.level === 'number' || typeof row.previousLevel === 'number';

export type SkillDiffTableProps = {
    readonly 'aria-label'?: string;
    readonly className?: string;
    readonly rows: readonly SkillDiffRowData[];
    readonly scaleCount?: number;
};

export function SkillDiffTable({
    'aria-label': ariaLabel = 'Skill changes',
    className,
    rows,
    scaleCount = 5,
}: SkillDiffTableProps) {
    if (rows.length === 0) return null;
    // Proficiency data is optional: drop the third column entirely when no row
    // carries a level, rather than leaving an empty gutter.
    const showProficiency = rows.some(rowHasLevel);
    return (
        <DiffTable
            aria-label={ariaLabel}
            className={className}
            columnWidths={showProficiency ? undefined : TWO_COLUMN_WIDTHS}
        >
            {rows.map((row) => {
                const badge = CHANGE_BADGE[row.change];
                const isRemove = row.change === 'remove';
                return (
                    <DiffTableRow
                        key={row.id}
                        muted={row.resolved}
                        left={<Badge variant={badge.variant}>{badge.label}</Badge>}
                        center={
                            <span className="flex min-w-0 flex-col gap-xxs">
                                <span
                                    className={clsx(
                                        'truncate text-sm font-medium leading-tight tracking-tight',
                                        isRemove ? 'text-black-400 line-through' : 'text-black-900',
                                    )}
                                >
                                    {row.title}
                                </span>
                                {row.typeLabel !== undefined && row.typeLabel.length > 0 ? (
                                    <span className="truncate text-xs leading-tight text-black-400">
                                        {row.typeLabel}
                                    </span>
                                ) : null}
                            </span>
                        }
                        right={
                            showProficiency ? (
                                <SkillDiffProficiency
                                    change={row.change}
                                    level={row.level}
                                    previousLevel={row.previousLevel}
                                    scaleCount={scaleCount}
                                />
                            ) : undefined
                        }
                    />
                );
            })}
        </DiffTable>
    );
}
